import { PartialMessage } from '@bufbuild/protobuf'
import { PaymentCollectionType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_collection_type_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ConditionalWrapper } from '@/components/shared/conditional-wrapper'
import { TooltipWrapper } from '@/components/shared/tooltip-wrapper'
import { Badge } from '@/components/ui/badge'
import { formatPriceInCents } from '@/lib/dinero/utils'
import { computeBasedOnPercent } from '@/lib/utils/percentage'

type PriceBadgeProps = {
  storeConfig?: StoreConfig
  priceInCents: number
  depositInCents?: number
  isCustom?: boolean
}

export const PriceBadge = ({
  storeConfig,
  priceInCents,
  depositInCents,
  isCustom,
}: PriceBadgeProps) => {
  const { t } = useTranslation()
  return (
    <ConditionalWrapper
      condition={!!isCustom}
      wrapper={(children) => {
        return (
          <TooltipWrapper message={t('Custom price applied')}>
            {children}
          </TooltipWrapper>
        )
      }}
    >
      <Badge variant="green" className="h-9 p-2 text-sm">
        {storeConfig?.paymentCollectionType ===
          PaymentCollectionType.COLLECT_UPFRONT &&
        !!depositInCents &&
        priceInCents !== depositInCents ? (
          <>
            {formatPriceInCents(priceInCents, storeConfig?.currency, true)},{' '}
            {formatPriceInCents(depositInCents, storeConfig?.currency, false)}{' '}
            Deposit
          </>
        ) : (
          <>
            {formatPriceInCents(
              priceInCents,
              storeConfig?.currency || 'usd',
              true,
            )}
          </>
        )}
        {isCustom && ' *'}
      </Badge>
    </ConditionalWrapper>
  )
}

type EventTypePriceBadgeProps = {
  eventType: PartialMessage<EventType>
  storeConfig?: StoreConfig
}

export const EventTypePriceBadge = ({
  eventType,
  storeConfig,
}: EventTypePriceBadgeProps) => {
  if (!eventType || !eventType.priceInCents || !eventType.currency) return null

  const depositInCents = computeBasedOnPercent({
    priceInCents: eventType.priceInCents,
    percentage: storeConfig?.depositPercent,
  })

  return (
    <Badge variant="green" className="h-9 p-2 text-sm">
      {storeConfig?.paymentCollectionType ===
        PaymentCollectionType.COLLECT_UPFRONT &&
      !!depositInCents &&
      eventType.priceInCents !== depositInCents ? (
        <>
          {formatPriceInCents(eventType.priceInCents, eventType.currency, true)}
          , {formatPriceInCents(depositInCents, eventType.currency, false)}{' '}
          Deposit
        </>
      ) : (
        <>
          {formatPriceInCents(eventType.priceInCents, eventType.currency, true)}
        </>
      )}
    </Badge>
  )
}
